import { Permission, RoleLevel } from "helpers/utils/rbac";
import { lazy } from "react";
import { Navigate } from "react-router-dom";

// ... (Imports wese hi rahenge) ...
// Lazy-loaded Pages (Imports same as your code)
const Dashboard = lazy(() => import("../pages/Dashboard/index"));
const LeadCalendarController = lazy(
  () => import("../pages/Dashboard/Controller/LeadCalendarController"),
);
const Error404 = lazy(() => import("../pages/AuthenticationInner/Errors/Basic404"));
const Error500 = lazy(() => import("../pages/AuthenticationInner/Errors/Error500"));
const Offlinepage = lazy(() => import("../pages/AuthenticationInner/Errors/Offlinepage"));
const Unauthorized = lazy(() => import("../pages/Unauthorized/index"));
const Login = lazy(() => import("../pages/Authentication/Login"));
const ForgetPasswordPage = lazy(() => import("../pages/Authentication/ForgetPassword"));
const ResetPasswordPage = lazy(() => import("../pages/Authentication/ResetPassword"));

const Logout = lazy(() => import("../pages/Authentication/Logout"));
const Register = lazy(() => import("../pages/Authentication/Register"));
const UserProfile = lazy(() => import("../pages/Authentication/user-profile"));
const Projects = lazy(() => import("../pages/Projects"));
const AddProjects = lazy(() => import("../pages/Projects/Controller/ProjectFormController"));
const ViewProjects = lazy(() => import("../pages/Projects/View/ProjectView.jsx"));
const User = lazy(() => import("../pages/MasterComponents/User"));
const AddUser = lazy(() => import("../pages/MasterComponents/User/Controller/UserController"));
const Locations = lazy(() => import("../pages/MasterComponents/Locations/index.jsx"));
const Designations = lazy(() => import("../pages/MasterComponents/Designations/index.jsx"));
const Amenities = lazy(() => import("../pages/MasterComponents/Amenities/index.jsx"));
const DocumentType = lazy(() => import("../pages/MasterComponents/DocumentType/index.jsx"));
const Bank = lazy(() => import("../pages/MasterComponents/Bank/index"));
const EmployeeAdd = lazy(
  () => import("../pages/MasterComponents/Employee/Controller/EmpFormController.jsx"),
);
const Employee = lazy(() => import("../pages/MasterComponents/Employee/index.jsx"));
const EmployeesDetailView = lazy(
  () => import("../pages/MasterComponents/Employee/View/EmployeesDetailView.jsx"),
);
const AddBank = lazy(() => import("../pages/MasterComponents/Bank/Controller/BankFormController"));
const Roles = lazy(() => import("../pages/MasterComponents/Roles/index.jsx"));
const RoleFormPage = lazy(
  () => import("../pages/MasterComponents/Roles/Controller/RoleFormPage.jsx"),
);
const ViewRole = lazy(
  () => import("../pages/MasterComponents/Roles/Controller/RoleViewController"),
);
const PermissionPage = lazy(() => import("../pages/MasterComponents/Permission/index.jsx"));
const Sources = lazy(() => import("../pages/MasterComponents/Sources/index.jsx"));
const Company = lazy(() => import("../pages/MasterComponents/Company/index.jsx"));
const Developer = lazy(() => import("../pages/Developer"));
const AddDeveloper = lazy(() => import("../pages/Developer/Controller/DeveloperFormController.js"));
const DeveloperView = lazy(() => import("../pages/Developer/View/DeveloperView.jsx"));
const InquiryView = lazy(() => import("../pages/Inquirys/index.js"));
const AddInquiry = lazy(() => import("../pages/Inquirys/Controller/InquiryFormController"));
const LeadDetailsPage = lazy(
  () => import("../pages/Inquirys/View/LeadDetailPage/LeadDetailsPage.jsx"),
);
const LeadExcelUploader = lazy(
  () => import("../pages/Inquirys/Controller/LeadExcelUploaderController.jsx"),
);
const LeadPipelineReport = lazy(() => import("../pages/LeadPipilineDashboard/index"));
const SiteVisitScheduledDashboard = lazy(() => import("../pages/SiteVisitDashboardNew/index"));
const SiteVisitScheduledReport = lazy(() => import("../pages/SiteVisitReport/index"));

const RMHourlyDashboard = lazy(() => import("../pages/RMHourlyDashboard/index"));

const authProtectedRoutes = [
  // Dashboard - No loader needed (or special dashboard loader)
  {
    path: "/dashboard",
    component: <Dashboard />,
    loaderType: "none",
  },
  {
    path: "/dashboard/lead-pipeline-report",
    component: <LeadPipelineReport />,
    loaderType: "table",
  },
  {
    path: "/dashboard/lead/calendar",
    component: <LeadCalendarController />,
    loaderType: "table",
  },
  {
    path: "/dashboard/site-visit-scheduled-dashboard",
    component: <SiteVisitScheduledDashboard />,
    loaderType: "table",
  },
  {
    path: "/dashboard/site-visit-scheduled-report",
    component: <SiteVisitScheduledReport />,
    loaderType: "table",
  },
  {
    path: "/dashboard/rm-calling",
    component: <RMHourlyDashboard />,
    loaderType: "table",
  },

  // Profile - Usually a form layout
  {
    path: "/profile",
    component: <UserProfile />,
    loaderType: "form",
  },

  // --- Masters (Lists are Tables) ---
  {
    path: "/master/user",
    component: <User />,
    permissions: [Permission.USERS_READ],
    loaderType: "table",
  },
  {
    path: "/master/user/add-user",
    component: <AddUser />,
    permissions: [Permission.USERS_CREATE],
    loaderType: "form",
  },
  {
    path: "/master/user/add-user/:id?",
    component: <AddUser />,
    permissions: [Permission.USERS_CREATE],
    loaderType: "form",
  },

  // --- Employee Forms ---
  {
    path: "/master/employee",
    component: <Employee />,
    loaderType: "table",
    roleLevel: RoleLevel.ORGANIZATION_ADMIN,
  },
  {
    path: "/master/employee/add-employee",
    component: <EmployeeAdd />,
    loaderType: "form",
    roleLevel: RoleLevel.ORGANIZATION_ADMIN,
  },
  {
    path: "/master/employee/add-employee/:id?",
    component: <EmployeeAdd />,
    loaderType: "form",
    roleLevel: RoleLevel.ORGANIZATION_ADMIN,
  },
  {
    path: "/master/employee/add-employee/view/:id?",
    component: <EmployeesDetailView />,
    loaderType: "form",
    roleLevel: RoleLevel.ORGANIZATION_ADMIN,
  },

  // --- Small Masters (Tables) ---
  {
    path: "/master/locations",
    component: <Locations />,
    loaderType: "table",
    roleLevel: RoleLevel.ORGANIZATION_ADMIN,
  },
  {
    path: "/master/designations",
    component: <Designations />,
    loaderType: "table",
    roleLevel: RoleLevel.ORGANIZATION_ADMIN,
  },
  {
    path: "/master/amenities",
    component: <Amenities />,
    loaderType: "table",
    roleLevel: RoleLevel.ORGANIZATION_ADMIN,
  },
  {
    path: "/master/documenttype",
    component: <DocumentType />,
    loaderType: "table",
    roleLevel: RoleLevel.ORGANIZATION_ADMIN,
  },
  {
    path: "/master/source",
    component: <Sources />,
    loaderType: "table",
    roleLevel: RoleLevel.ORGANIZATION_ADMIN,
  },
  {
    path: "/master/companies",
    component: <Company />,
    loaderType: "table",
    roleLevel: RoleLevel.ORGANIZATION_ADMIN,
  },

  // --- Bank ---
  {
    path: "/master/banks",
    component: <Bank />,
    loaderType: "table",
    roleLevel: RoleLevel.ORGANIZATION_ADMIN,
  },
  {
    path: "/master/add-bank",
    component: <AddBank />,
    loaderType: "form",
    roleLevel: RoleLevel.ORGANIZATION_ADMIN,
  },
  {
    path: "/master/add-bank/:id?",
    component: <AddBank />,
    loaderType: "form",
    roleLevel: RoleLevel.ORGANIZATION_ADMIN,
  },

  // --- Roles & Permissions ---
  {
    path: "/master/roles",
    component: <Roles />,
    permissions: [Permission.ROLES_READ],
    loaderType: "table",
  },
  {
    path: "/master/roles/add-role",
    component: <RoleFormPage />,
    permissions: [Permission.ROLES_CREATE],
    loaderType: "form",
  },
  {
    path: "/master/roles/add-role/:id?",
    component: <RoleFormPage />,
    permissions: [Permission.ROLES_UPDATE],
    loaderType: "form",
  },
  {
    path: "/master/roles/view-role/:id",
    component: <ViewRole />,
    permissions: [Permission.ROLES_READ],
    loaderType: "form",
  },
  {
    path: "/master/permission",
    component: <PermissionPage />,
    permissions: [Permission.PERMISSIONS_READ],
    loaderType: "table",
  },

  // --- Projects ---
  {
    path: "/project",
    component: <Projects />,
    permissions: [Permission.PROJECTS_READ],
    loaderType: "table",
  },
  {
    path: "/add-project",
    component: <AddProjects />,
    permissions: [Permission.PROJECTS_CREATE],
    loaderType: "form",
  },
  {
    path: "/add-project/:id?",
    component: <AddProjects />,
    permissions: [Permission.PROJECTS_UPDATE],
    loaderType: "form",
  },
  {
    path: "/project/view/:id?",
    component: <ViewProjects />,
    permissions: [Permission.PROJECTS_READ],
    loaderType: "form", // Detail view
  },

  // --- Developer ---
  {
    path: "/developer",
    component: <Developer />,
    permissions: [Permission.BUILDERS_READ],
    loaderType: "table",
  },
  {
    path: "/add-developer",
    component: <AddDeveloper />,
    permissions: [Permission.BUILDERS_CREATE],
    loaderType: "form",
  },
  {
    path: "/add-developer/:id?",
    component: <AddDeveloper />,
    permissions: [Permission.BUILDERS_UPDATE],
    loaderType: "form",
  },
  {
    path: "/developer/view/:id?",
    component: <DeveloperView />,
    permissions: [Permission.BUILDERS_READ],
    loaderType: "form",
  },

  // --- Inquiries ---
  {
    path: "/inquiries",
    component: <InquiryView />,
    permissions: [Permission.LEADS_READ],
    loaderType: "table",
  },
  {
    path: "/add-inquirie",
    component: <AddInquiry />,
    permissions: [Permission.LEADS_CREATE],
    loaderType: "form",
  },
  {
    path: "/edit-inquirie/:id?",
    component: <AddInquiry />,
    permissions: [Permission.LEADS_UPDATE],
    loaderType: "form",
  },
  {
    path: "/inquiries/details-inquiry/:id?",
    component: <LeadDetailsPage />,
    permissions: [Permission.LEADS_READ],
    loaderType: "general", // Detail view
  },
  {
    path: "/inquiries/excel-upload",
    component: <LeadExcelUploader />,
    permissions: [Permission.LEADS_IMPORT],
    loaderType: "form",
  },

  // Logout
  { path: "/logout", component: <Logout /> },

  // Unauthorized page
  { path: "/unauthorized", component: <Unauthorized /> },

  // Default redirect
  { path: "/", component: <Navigate to="/dashboard" replace /> },
];

const publicRoutes = [
  { path: "/login", component: <Login /> },
  { path: "/forgot-password", component: <ForgetPasswordPage /> },
  { path: "/reset-password", component: <ResetPasswordPage /> },
  { path: "/register", component: <Register /> },
  { path: "/auth-500", component: <Error500 /> },
  { path: "/auth-offline", component: <Offlinepage /> },
];

export { authProtectedRoutes, publicRoutes };
